import React, { useEffect, useState } from "react";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import { FaShoppingBasket } from "react-icons/fa";
import AdminColorBasketMiniMap, {
    colorBasketNumberBadgeSx,
} from "../../../admin/jigsaw/play/AdminColorBasketMiniMap";

const ACCENT = "#3BA666";
const INK = "#123524";
/** Each step stays lit this long, then the next one takes over, looping forever. */
const STEP_HIGHLIGHT_MS = 3000;

/** Same 1.1s beat as the flickering basket region on the map, so the pair reads as one thing. */
const swatchPulse = keyframes`
  0%, 100% {
    opacity: 1;
    box-shadow: 0 4px 12px rgba(0,0,0,0.22), 0 0 0 8px rgba(59,166,102,0.5);
  }
  50% {
    opacity: 0.3;
    box-shadow: 0 4px 12px rgba(0,0,0,0.22), 0 0 0 0 rgba(59,166,102,0);
  }
`;

const STEPS = [
    {
        id: "pick",
        icon: <FaShoppingBasket size={40} color="#fff" />,
        title: "Pick a color basket",
        body: "Every basket holds the pieces that share one color of the picture.",
    },
    {
        id: "fill",
        icon: <ViewInArOutlinedIcon sx={{ fontSize: 46, color: "#fff" }} />,
        title: "Fill that basket only",
        body: "A small board shows just those cells, so a 144-piece puzzle feels like a tiny one.",
    },
    {
        id: "deploy",
        icon: <RocketLaunchIcon sx={{ fontSize: 46, color: "#fff" }} />,
        title: "Deploy to the puzzle",
        body: "Finished basket pieces land on the main board, already at home.",
    },
];

/**
 * Color Basket feature shot for 1080p capture — headline, the three steps, and the puzzle
 * painted with its real basket regions. Fills the frame body it is placed in.
 */
export default function ColorBasketFeatureShot({
    baskets = [],
    imageUrl,
    grid,
    puzzleTitle = null,
    highlightBasketIndex = null,
}) {
    const swatches = baskets.slice(0, 8);
    /**
     * Basket colors on a photo are close to the photo itself, so painting every region reads as
     * noise. One emphasized region shows what a basket is; the swatch row shows there are more.
     */
    const highlightIndex = highlightBasketIndex ?? baskets[0]?.index ?? null;
    const highlighted =
        highlightIndex == null
            ? null
            : baskets.find((b) => Number(b?.index) === Number(highlightIndex)) ?? null;
    const highlightedCount = Array.isArray(highlighted?.pieceIds)
        ? highlighted.pieceIds.length
        : 0;

    const [activeStep, setActiveStep] = useState(0);
    useEffect(() => {
        const timer = setInterval(
            () => setActiveStep((s) => (s + 1) % STEPS.length),
            STEP_HIGHLIGHT_MS,
        );
        return () => clearInterval(timer);
    }, []);

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                px: 8,
            }}
        >
            <Box sx={{ flex: "1 1 0", minWidth: 0, maxWidth: 900 }}>
                <Typography
                    component="p"
                    sx={{
                        fontSize: 34,
                        fontWeight: 800,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: ACCENT,
                        mb: 1.5,
                    }}
                >
                    Jigsaw Muse
                </Typography>
                <Typography
                    component="h1"
                    sx={{ fontSize: 104, fontWeight: 900, lineHeight: 1.02, color: INK }}
                >
                    Color Baskets
                </Typography>
                <Typography
                    component="p"
                    sx={{ fontSize: 44, fontWeight: 600, color: "rgba(18,53,36,0.72)", mt: 2, mb: 5 }}
                >
                    Big puzzles, one color at a time.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {STEPS.map((step, i) => {
                        const isActive = i === activeStep;
                        return (
                            <Box
                                key={step.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 3,
                                    p: 2,
                                    borderRadius: 4,
                                    bgcolor: isActive ? "rgba(255,255,255,0.9)" : "transparent",
                                    boxShadow: isActive
                                        ? "0 12px 30px rgba(0,0,0,0.16)"
                                        : "none",
                                    opacity: isActive ? 1 : 0.42,
                                    transform: isActive ? "scale(1)" : "scale(0.97)",
                                    transformOrigin: "left center",
                                    transition:
                                        "opacity 420ms ease, transform 420ms ease, background-color 420ms ease, box-shadow 420ms ease",
                                }}
                            >
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: "relative",
                                        width: 88,
                                        height: 88,
                                        borderRadius: 3,
                                        flexShrink: 0,
                                        bgcolor: ACCENT,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: isActive
                                            ? `0 6px 18px rgba(0,0,0,0.22), 0 0 0 8px ${ACCENT}33`
                                            : "0 6px 18px rgba(0,0,0,0.18)",
                                        transition: "box-shadow 420ms ease",
                                    }}
                                >
                                    {step.icon}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: -10,
                                            left: -10,
                                            ...colorBasketNumberBadgeSx({
                                                size: 38,
                                                highlighted: isActive,
                                            }),
                                        }}
                                    >
                                        {i + 1}
                                    </Box>
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        component="p"
                                        sx={{
                                            fontSize: 42,
                                            fontWeight: 800,
                                            color: INK,
                                            lineHeight: 1.15,
                                        }}
                                    >
                                        {step.title}
                                    </Typography>
                                    <Typography
                                        component="p"
                                        sx={{
                                            fontSize: 30,
                                            fontWeight: 500,
                                            color: "rgba(18,53,36,0.66)",
                                            mt: 0.75,
                                        }}
                                    >
                                        {step.body}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box
                sx={{
                    flex: "0 0 auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        width: 760,
                        p: 2.5,
                        borderRadius: 5,
                        bgcolor: "rgba(255,255,255,0.82)",
                        boxShadow: "0 14px 40px rgba(0,0,0,0.18)",
                    }}
                >
                    {imageUrl && grid > 0 ? (
                        <AdminColorBasketMiniMap
                            baskets={baskets}
                            imageUrl={imageUrl}
                            grid={grid}
                            size={null}
                            compact
                            oneBasedLabels
                            highlightBasketIndex={highlightIndex}
                            emphasizeOnly={highlightIndex != null}
                            pulseHighlight
                        />
                    ) : null}
                </Box>
                {highlighted ? (
                    <Typography
                        component="p"
                        sx={{ fontSize: 34, fontWeight: 800, color: ACCENT }}
                    >
                        {`Basket ${Number(highlighted.index) + 1} — ${highlightedCount} pieces`}
                    </Typography>
                ) : null}
                <Box sx={{ display: "flex", gap: 2, py: 1 }}>
                    {swatches.map((b, i) => {
                        const isHighlighted =
                            highlightIndex != null && Number(b?.index) === Number(highlightIndex);
                        return (
                            <Box
                                key={b?.index ?? i}
                                aria-hidden
                                sx={{
                                    position: "relative",
                                    width: 74,
                                    height: 74,
                                    borderRadius: 2.5,
                                    bgcolor: b?.dominantColorHex || "#999999",
                                    border: "3px solid rgba(255,255,255,0.9)",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.22)",
                                    ...(isHighlighted
                                        ? {
                                              animation: `${swatchPulse} 1.1s ease-in-out infinite`,
                                          }
                                        : null),
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 4,
                                        left: 4,
                                        ...colorBasketNumberBadgeSx({
                                            size: 26,
                                            highlighted: isHighlighted,
                                        }),
                                    }}
                                >
                                    {Number(b?.index ?? i) + 1}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
                {puzzleTitle ? (
                    <Typography
                        component="p"
                        sx={{ fontSize: 28, fontWeight: 700, color: "rgba(18,53,36,0.6)" }}
                    >
                        {puzzleTitle}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
